import MomentUtils from "@date-io/moment";
import { CircularProgress, TextField } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { Button } from "@material-ui/core";
import { Theme, makeStyles } from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import { Dialog } from "@material-ui/core";
import { DialogActions } from "@material-ui/core";
import { DialogContent } from "@material-ui/core";
import { DialogContentText } from "@material-ui/core";
import { DialogTitle } from "@material-ui/core";
import {
	KeyboardDatePicker,
	KeyboardTimePicker,
	MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import * as React from "react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { useShiftsCrewsContext } from "../../context/ShiftsCrewsContext";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { ShiftParameters } from "../../models/ShiftParameters";
import { DeleteShiftParameters } from "../../repositories/ShiftsCrewsRepository";

const useStyles = makeStyles((theme: Theme) => ({
	title: {
		fontSize: 15,
	},
	modalTitle: {
		fontSize: 15,
		marginTop: theme.spacing(1),
	},
	chip: {
		margin: 2,
	},
	errorLabel: {
		fontSize: 12,
		color: "#F44336",
	},
	infoLabel: {
		fontSize: 12,
		color: "#757575",
	},
}));

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const DeleteShift = ({ show, onHide }: Props) => {
	const [open, setOpen] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [deleteAlert, setDeleteAlert] = useState(false);

	const classes = useStyles();
	const {
		state: { shiftTableData },
	} = useShiftsCrewsContext();
	const [error, setError] = useState("");

	const {
		control,
		setValue,
		reset,
		watch,
		formState: { errors },
	} = useForm<ShiftParameters>({ defaultValues: INITIAL_VALUES });

	const onDelete: (data: Date) => Promise<void> = async (data: Date) => {
		setIsSubmitLoading(true);

		const shiftParameters = await DeleteShiftParameters(data as Date);
		if (shiftParameters.ok) {
			setIsSubmitLoading(false);
			setOpen(true);
			onHide(true);
			setDeleteAlert(false);
		} else {
			setError(shiftParameters.message);
			setIsSubmitLoading(false);
			setDeleteAlert(false);
		}
	};

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
		setDeleteAlert(false);
	};

	const patternStart = watch("PatternStart");
	const shiftsPerDay = watch("ShiftsPerDay");
	const crewRotation = watch("CrewRotation");
	const numberOfCrews = watch("NumberOfCrews");
	const crewsCodes = watch("Crews");
	const rotationPattern = watch("PatternDays");

	useEffect(() => {
		if (show) {
			reset();
			setValue("PatternStart", shiftTableData.PatternStart);
			setValue("ShiftCodes", shiftTableData.ShiftCodes);
			setValue("Crews", shiftTableData.Crews);
			setValue("PatternDays", shiftTableData.PatternDays);
			setValue("CrewRotation", shiftTableData.CrewRotation);
			setValue("Comments", shiftTableData.Comments);
		}
	}, [show]);

	return (
		<>
			<Grid container>
				<Grid item>
					<MesfModal
						title="DELETE SHIFT"
						open={show}
						handleClose={() => onHide(false)}
						id="delete-shift-MesfModal"
						maxWidth="md"
					>
						<form>
							<MesfModal.Content style={{ padding: "15px 30px 0" }}>
								<Grid container spacing={2}>
									<Controller
										name="PatternStart"
										control={control}
										render={({ field: { value, onChange } }) => (
											<MuiPickersUtilsProvider utils={MomentUtils}>
												<Grid item md={6} xs={12} style={{ margin: "8px 0 0" }}>
													<KeyboardDatePicker
														fullWidth
														disableToolbar
														variant="inline"
														format="MM/DD/yyyy"
														id="date-picker-inline"
														style={{
															minWidth: "100%",
															borderTopLeftRadius: 3,
															borderTopRightRadius: 3,
														}}
														label="Start"
														value={value}
														disabled={!!shiftTableData}
														onChange={(evt: any) => onChange(evt?.toDate())}
														KeyboardButtonProps={{
															"aria-label": "change date",
														}}
													/>
												</Grid>
												<Grid
													item
													md={6}
													xs={12}
													style={{ margin: "8px 0 18px" }}
												>
													<KeyboardTimePicker
														fullWidth
														variant="inline"
														id="time-picker"
														label="Time"
														value={value}
														disabled={!!shiftTableData}
														onChange={(evt: any) => onChange(evt?.toDate())}
														KeyboardButtonProps={{
															"aria-label": "change time",
														}}
													/>
												</Grid>
											</MuiPickersUtilsProvider>
										)}
									/>

									<Grid item md={6} xs={6}>
										<Controller
											name="ShiftsPerDay"
											control={control}
											rules={{
												min: 1,
											}}
											render={({ field: { onChange, value } }) => (
												<TextField
													label={`# ${
														shiftsPerDay > 1 ? "Shifts" : "Shift"
													}  per day`}
													variant="outlined"
													error={!!errors.ShiftsPerDay}
													fullWidth
													margin="dense"
													autoComplete="off"
													type="number"
													value={crewRotation.length / rotationPattern}
													disabled={!!shiftTableData}
												/>
											)}
										/>
										{errors.ShiftsPerDay?.type === "min" && (
											<span className={classes.errorLabel}>
												Shifts per day is required
											</span>
										)}
									</Grid>
									<Grid item md={6} xs={6} style={{ paddingBottom: "0" }}>
										<Controller
											name="ShiftCodes"
											control={control}
											render={({ field: { value } }) => (
												<TextField
													label="Shifts Ids"
													variant="outlined"
													error={!!errors.ShiftCodes}
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													disabled={!!shiftTableData}
												/>
											)}
										/>
									</Grid>
									<Grid item md={6} xs={6}>
										<Controller
											name="NumberOfCrews"
											control={control}
											rules={{
												min: 1,
												max: 9,
											}}
											render={({ field: { value } }) => (
												<TextField
													label={`# ${numberOfCrews > 1 ? "Crews" : "Crew"}`}
													variant="outlined"
													error={!!errors.NumberOfCrews}
													fullWidth
													margin="dense"
													autoComplete="off"
													type="number"
													value={crewsCodes.length}
													disabled={!!shiftTableData}
												/>
											)}
										/>
									</Grid>
									<Grid item md={6} xs={6} style={{ paddingBottom: "0" }}>
										<Controller
											name="Crews"
											control={control}
											render={({ field: { value } }) => (
												<TextField
													label="Crews Ids"
													variant="outlined"
													error={!!errors.Crews}
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													disabled={!!shiftTableData}
												/>
											)}
										/>
									</Grid>
									<Grid item md={6} xs={6}>
										<Controller
											name="PatternDays"
											control={control}
											render={({ field: { value } }) => (
												<TextField
													label="Rotation Pattern"
													variant="outlined"
													error={!!errors.PatternDays}
													fullWidth
													type="number"
													margin="dense"
													autoComplete="off"
													value={value}
													disabled={!!shiftTableData}
												/>
											)}
										/>
									</Grid>
									<Grid item md={12} xs={12}>
										<Controller
											name="CrewRotation"
											control={control}
											render={({ field: { value, onChange } }) => (
												<TextField
													label="Crew rotation"
													variant="outlined"
													fullWidth
													margin="dense"
													autoComplete="off"
													value={value}
													disabled={!!shiftTableData}
												/>
											)}
										/>
									</Grid>
									<Grid item md={12} xs={12}>
										<Controller
											name="Comments"
											control={control}
											render={({ field: { value, onChange } }) => (
												<TextField
													style={{ margin: "8px 0" }}
													size="medium"
													maxRows={5}
													label="Comments"
													multiline
													variant="outlined"
													fullWidth
													autoComplete="off"
													value={value}
													disabled={!!shiftTableData}
												/>
											)}
										/>
									</Grid>
								</Grid>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
								<Grid container spacing={2} justifyContent="flex-end">
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="default"
											onClick={() => onHide(false)}
										>
											Cancel
										</Button>
									</Grid>
									<Grid item md={3} xs={12} style={{ margin: 0 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={
												isSubmitLoading
													? isSubmitLoading
													: new Date(shiftTableData.PatternStart) <
															new Date() && true
											}
											variant="contained"
											color="secondary"
											onClick={() => setDeleteAlert(true)}
										>
											Delete
										</Button>
										{new Date(shiftTableData.PatternStart) < new Date() && (
											<Grid item>
												<span
													className={classes.errorLabel}
													style={{ fontSize: 10 }}
												>
													Can not delete before the current date
												</span>
											</Grid>
										)}
									</Grid>
								</Grid>
							</MesfModal.Actions>
							<Dialog
								open={deleteAlert}
								onClose={() => handleClose()}
								aria-labelledby="responsive-dialog-title"
							>
								<DialogTitle id="responsive-dialog-title">
									{"WARNING"}
								</DialogTitle>
								<DialogContent>
									<DialogContentText style={{ padding: 0, margin: 0 }}>
										Be careful, once deleted you will not be able to recover
										this shift, are you sure you want to delete it?
									</DialogContentText>
								</DialogContent>
								<DialogActions style={{ padding: "16px 24px" }}>
									<Button
										onClick={() => setDeleteAlert(false)}
										autoFocus
										color="default"
									>
										CANCEL
									</Button>
									<Button
										startIcon={
											isSubmitLoading && <CircularProgress size="1rem" />
										}
										autoFocus
										color="secondary"
										onClick={() => onDelete(patternStart as Date)}
									>
										Delete
									</Button>
								</DialogActions>
							</Dialog>
						</form>
					</MesfModal>
				</Grid>
			</Grid>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<Alert severity="warning" onClose={handleClose}>
					The shift was deleted successfully
				</Alert>
			</Snackbar>
		</>
	);
};
